/**
 * ReportViewer - Renders report data as chart or table
 *
 * v3.53 - 2026-02-24 - Story 13: Import shared utils from chartUtils.ts (ADR-002)
 * v3.28 - 2026-02-22 - Improve column labels (ABBREVIATION_MAP), add axis labels to charts
 * v3.25 - 2026-02-19 - Hide internal table names, show layman category label
 * v2.97 - 2026-02-07 - Report viewer with table, bar, line, pie charts
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  TableChart as TableChartIcon,
  PieChart as PieChartIcon,
  ShowChart as LineChartIcon,
} from '@mui/icons-material';
import { reportService, Report, ReportDataResponse } from '../services/api';
import { formatTitle, formatColumnLabel } from '../utils/chartUtils'; // v3.53 - Story 13: shared utils

interface ReportViewerProps {
  report: Report;
  onClose: () => void;
}

const chartIcons: Record<string, React.ReactElement> = {
  bar: <BarChartIcon />,
  table: <TableChartIcon />,
  pie: <PieChartIcon />,
  line: <LineChartIcon />,
};

const ReportViewer: React.FC<ReportViewerProps> = ({ report, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ReportDataResponse | null>(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const result = await reportService.getData(report.id);
        setData(result);
      } catch (err: any) {
        setError(err.message || 'Failed to load report data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [report.id]);

  const formatCellValue = (value: any): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    return String(value);
  };

  // Render a simple data table
  const renderTable = () => {
    if (!data || data.data.length === 0) {
      return <Alert severity="info">No data available for this report.</Alert>;
    }
    const columns = data.columns || report.columns.map((c) => ({ name: c, type: 'string' }));
    return (
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {/* v3.28 - Layman column labels via ABBREVIATION_MAP */}
              {columns.map((col) => (
                <TableCell key={col.name} sx={{ fontWeight: 600 }}>
                  {formatColumnLabel(col.name)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.data.map((row, i) => (
              <TableRow key={i} hover>
                {columns.map((col) => (
                  <TableCell key={col.name}>{formatCellValue(row[col.name])}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Render a simple bar chart using CSS
  const renderBarChart = () => {
    if (!data || data.data.length === 0) {
      return <Alert severity="info">No data available for this report.</Alert>;
    }
    // Use first column as label, find first numeric column for values
    const columns = data.columns || report.columns.map((c) => ({ name: c, type: 'string' }));
    const labelCol = columns[0]?.name;
    const valueCol = columns.find((c) => c.type === 'number' || c.type === 'float' || c.type === 'integer')?.name
      || columns[1]?.name;

    if (!labelCol || !valueCol) return renderTable();

    const values = data.data.map((row) => Number(row[valueCol]) || 0);
    const maxVal = Math.max(...values, 1);

    return (
      <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
        {/* v3.28 - Axis labels */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Typography variant="caption" sx={{ minWidth: 120, textAlign: 'right', fontWeight: 600, color: 'text.secondary' }}>
            {formatColumnLabel(labelCol)}
          </Typography>
          <Typography variant="caption" sx={{ flex: 1, fontWeight: 600, color: 'text.secondary' }}>
            {formatColumnLabel(valueCol)}
          </Typography>
        </Box>
        {data.data.slice(0, 20).map((row, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="caption" sx={{ minWidth: 120, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {formatCellValue(row[labelCol])}
            </Typography>
            <Box sx={{ flex: 1, height: 24, bgcolor: 'grey.100', borderRadius: 1, overflow: 'hidden' }}>
              <Box sx={{
                height: '100%',
                width: `${(values[i] / maxVal) * 100}%`,
                bgcolor: 'primary.main',
                borderRadius: 1,
                minWidth: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                px: 0.5,
              }}>
                <Typography variant="caption" sx={{ color: 'primary.contrastText', fontSize: '0.65rem' }}>
                  {values[i].toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  // Render a simple line chart using SVG
  const renderLineChart = () => {
    if (!data || data.data.length === 0) {
      return <Alert severity="info">No data available for this report.</Alert>;
    }
    const columns = data.columns || report.columns.map((c) => ({ name: c, type: 'string' }));
    const valueCol = columns.find((c) => c.type === 'number' || c.type === 'float' || c.type === 'integer')?.name
      || columns[1]?.name;

    if (!valueCol) return renderTable();

    const values = data.data.map((row) => Number(row[valueCol]) || 0);
    const maxVal = Math.max(...values, 1);
    const minVal = Math.min(...values, 0);
    const range = maxVal - minVal || 1;
    const width = 600;
    const height = 200;
    const padding = 20;

    const points = values.map((v, i) => {
      const x = padding + (i / Math.max(values.length - 1, 1)) * (width - 2 * padding);
      const y = height - padding - ((v - minVal) / range) * (height - 2 * padding);
      return `${x},${y}`;
    });

    return (
      <Box sx={{ textAlign: 'center' }}>
        {/* v3.28 - Y-axis label */}
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
          {formatColumnLabel(valueCol)}
        </Typography>
        <svg viewBox={`0 0 ${width} ${height}`} style={{ maxWidth: '100%', height: 'auto' }}>
          <polyline
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            points={points.join(' ')}
            style={{ color: 'var(--mui-palette-primary-main, #1976d2)' }}
          />
          {values.map((v, i) => {
            const x = padding + (i / Math.max(values.length - 1, 1)) * (width - 2 * padding);
            const y = height - padding - ((v - minVal) / range) * (height - 2 * padding);
            return <circle key={i} cx={x} cy={y} r="3" fill="currentColor" style={{ color: 'var(--mui-palette-primary-main, #1976d2)' }} />;
          })}
        </svg>
      </Box>
    );
  };

  // Render a simple pie chart using SVG
  const renderPieChart = () => {
    if (!data || data.data.length === 0) {
      return <Alert severity="info">No data available for this report.</Alert>;
    }
    const columns = data.columns || report.columns.map((c) => ({ name: c, type: 'string' }));
    const labelCol = columns[0]?.name;
    const valueCol = columns.find((c) => c.type === 'number' || c.type === 'float' || c.type === 'integer')?.name
      || columns[1]?.name;

    if (!labelCol || !valueCol) return renderTable();

    const items = data.data.slice(0, 8).map((row) => ({
      label: String(row[labelCol] || ''),
      value: Math.abs(Number(row[valueCol]) || 0),
    }));
    const total = items.reduce((sum, item) => sum + item.value, 0) || 1;

    const colors = ['#1976d2', '#388e3c', '#f57c00', '#d32f2f', '#7b1fa2', '#0097a7', '#455a64', '#c2185b'];
    const cx = 150, cy = 150, r = 120;
    let cumAngle = -Math.PI / 2;

    const slices = items.map((item, i) => {
      const angle = (item.value / total) * 2 * Math.PI;
      const x1 = cx + r * Math.cos(cumAngle);
      const y1 = cy + r * Math.sin(cumAngle);
      const x2 = cx + r * Math.cos(cumAngle + angle);
      const y2 = cy + r * Math.sin(cumAngle + angle);
      const largeArc = angle > Math.PI ? 1 : 0;
      const path = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
      cumAngle += angle;
      return { path, color: colors[i % colors.length], label: item.label, pct: ((item.value / total) * 100).toFixed(1) };
    });

    return (
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
        <svg viewBox="0 0 300 300" style={{ width: 250, height: 250 }}>
          {slices.map((s, i) => (
            <path key={i} d={s.path} fill={s.color} stroke="#fff" strokeWidth="1" />
          ))}
        </svg>
        <Box>
          {slices.map((s, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />
              <Typography variant="caption">{s.label} ({s.pct}%)</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const chartRenderers: Record<string, () => React.ReactNode> = {
    table: renderTable,
    bar: renderBarChart,
    line: renderLineChart,
    pie: renderPieChart,
  };

  return (
    <>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {chartIcons[report.chartType] || <TableChartIcon />}
          <Typography variant="h6" component="span">{report.title}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {report.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{report.description}</Typography>
        )}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
          <Chip label={report.chartType} size="small" variant="outlined" />
          <Chip label={formatTitle(report.goldTable)} size="small" color="primary" variant="outlined" />
          {report.columns.length > 0 && (
            <Chip label={`${report.columns.length} columns`} size="small" variant="outlined" />
          )}
          {data && <Chip label={`${data.total} rows`} size="small" variant="outlined" />}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          (chartRenderers[report.chartType] || renderTable)()
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </>
  );
};

export default ReportViewer;
